
import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Lock, Mail, Loader2 } from 'lucide-react';

interface LoginProps {
  onLogin: (email: string, pass: string) => Promise<boolean>;
  isAuth: boolean;
}

const Login: React.FC<LoginProps> = ({ onLogin, isAuth }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  if (isAuth) return <Navigate to="/admin" />;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    setIsLoading(true);

    try {
      await onLogin(email, password);
      // La redirection est gérée par le changement d'état isAuth dans App.tsx
    } catch (err: any) {
      setErrorMsg(err.message || 'Identifiants incorrects ou problème de connexion.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-offwhite px-6">
      <div className="max-w-md w-full bg-white p-12 shadow-xl border border-gray-100 animate-in fade-in zoom-in duration-700">
        <div className="text-center mb-10">
          <div className="inline-flex p-4 bg-brand-cream rounded-full mb-6 text-brand-gold">
            <Lock size={32} />
          </div>
          <h1 className="font-serif text-3xl mb-2">Gestion Khamal Bags</h1>
          <p className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">Espace Réservé Directoire</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-2">Adresse Email</label>
            <div className="relative">
              <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" />
              <input 
                type="email"
                className={`w-full p-4 pl-12 bg-gray-50 border ${errorMsg ? 'border-red-500' : 'border-gray-100'} outline-none focus:border-brand-gold luxury-transition text-sm`}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@khamal.store"
                required
                disabled={isLoading}
              />
            </div>
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-2">Mot de passe</label>
            <div className="relative">
              <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" />
              <input 
                type="password"
                className={`w-full p-4 pl-12 bg-gray-50 border ${errorMsg ? 'border-red-500' : 'border-gray-100'} outline-none focus:border-brand-gold luxury-transition text-sm`}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                disabled={isLoading}
              />
            </div>
            {errorMsg && (
              <p className="text-red-500 text-[9px] mt-4 uppercase tracking-widest font-bold text-center bg-red-50 p-2 border border-red-100">
                {errorMsg}
              </p>
            )}
          </div>

          <button 
            type="submit"
            disabled={isLoading}
            className="w-full bg-brand-black text-white py-4 text-[11px] uppercase tracking-[0.3em] font-bold hover:bg-brand-gold transition-colors duration-500 mt-4 shadow-lg active:scale-95 disabled:opacity-50 flex items-center justify-center gap-3"
          >
            {isLoading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Vérification...
              </>
            ) : (
              "S'authentifier"
            )}
          </button>
        </form>

        <div className="mt-10 text-center">
          <a href="#/" className="text-[9px] uppercase tracking-[0.2em] font-bold text-gray-400 hover:text-brand-black luxury-transition border-b border-gray-100 pb-1">
            Retour à la boutique publique
          </a>
        </div>
      </div>
    </div>
  );
};

export default Login;
